import { useEffect, useState } from "react";
import { getProfile, saveProfile } from "../backend";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const { user } = useAuth();
  const { isPremium, subscription } = useSubscription();
  const nav = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [location, setLocation] = useState("");
  const [texture, setTexture] = useState("Soft");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    async function loadProfile() {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const profile = await getProfile(user.uid);

        if (profile) {
          setFirstName(profile.firstName || "");
          setLastName(profile.lastName || "");
          setCuisine(profile.cuisine || "");
          setLocation(profile.location || "");
          setTexture(profile.texture || "Soft");
        }
      } catch (error) {
        setMessage("Unable to load your profile right now.");
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user?.uid]);

  const handleSaveProfile = async () => {
    if (!user?.uid) return;

    setSaving(true);
    setMessage("");

    try {
      const existingProfile = (await getProfile(user.uid)) || {};

      await saveProfile(user.uid, {
        ...existingProfile,
        email: user.email || existingProfile.email || "",
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        cuisine: cuisine.trim(),
        location: location.trim(),
        texture,
        updatedAt: new Date()
      });

      setMessage("Profile updated!");
    } catch (error) {
      setMessage("Unable to save your profile right now.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={{ color: "#64748b" }}>Loading profile...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.pageTitle}>Your Profile</h1>
      <p style={styles.pageSubtitle}>Manage your preferences and subscription</p>

      <div style={styles.card}>
        <p style={styles.cardTitle}>Personal info</p>

        <p style={styles.label}>First Name</p>
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          style={styles.input}
          placeholder="First name"
        />

        <p style={styles.label}>Last Name</p>
        <input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          style={styles.input}
          placeholder="Last name"
        />

        <p style={styles.label}>Favourite Cuisine</p>
        <input
          value={cuisine}
          onChange={(e) => setCuisine(e.target.value)}
          style={styles.input}
          placeholder="e.g. Indian, Italian"
        />

        <p style={styles.label}>Location</p>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          style={styles.input}
          placeholder="City or region"
        />

        <p style={styles.label}>Preferred Texture</p>
        <select
          value={texture}
          onChange={(e) => setTexture(e.target.value)}
          style={styles.input}
        >
          <option>Soft</option>
          <option>Crispy</option>
          <option>Chewy</option>
          <option>Crunchy</option>
        </select>

        {message && <p style={styles.message}>{message}</p>}

        <button
          onClick={handleSaveProfile}
          style={styles.button}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save changes"}
        </button>
      </div>

      {/* Subscription section */}
      <div style={styles.card}>
        <p style={styles.cardTitle}>Subscription</p>
        {isPremium ? (
          <>
            <div style={styles.premiumBadge}>
              ✅ Premium{" "}
              {subscription?.status === "trialing"
                ? "· Free trial"
                : subscription?.plan === "month" ? "· $3/mo" : "· $24/yr"}
            </div>
            <p style={{ margin: "0 0 8px", fontSize: 14, color: "#475569" }}>
              You have full access to AI recipes and dish photos.
            </p>
            <button
              style={styles.manageBtn}
              disabled={portalLoading}
              onClick={async () => {
                setPortalLoading(true);
                const res = await fetch("/api/customer-portal", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ userId: user.uid }),
                });
                const data = await res.json();
                if (data.url) window.location.href = data.url;
                else setPortalLoading(false);
              }}
            >
              {portalLoading ? "Loading..." : "Manage / Cancel subscription →"}
            </button>
          </>
        ) : (
          <>
            <p style={{ margin: "0 0 14px", fontSize: 14, color: "#475569" }}>
              You're on the free plan. Upgrade to unlock AI-generated recipes and dish photos.
            </p>
            <button style={styles.upgradeBtn} onClick={() => nav("/upgrade")}>
              Upgrade to Premium — 7-day free trial
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 520,
    margin: "0 auto",
    padding: "24px 20px",
    fontFamily: "'Inter', system-ui, sans-serif"
  },
  pageTitle: {
    margin: "0 0 4px",
    fontSize: 26,
    fontWeight: 800,
    color: "#0f172a"
  },
  pageSubtitle: {
    margin: "0 0 24px",
    fontSize: 15,
    color: "#64748b"
  },
  card: {
    marginTop: 16,
    padding: "24px 22px",
    borderRadius: 20,
    boxShadow: "0 8px 32px rgba(15,23,42,0.07)",
    background: "rgba(255,255,255,0.90)",
    border: "1px solid rgba(255,255,255,0.7)",
    backdropFilter: "blur(8px)",
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  cardTitle: {
    margin: "0 0 4px",
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a"
  },
  label: {
    margin: "4px 0 4px",
    fontSize: 13,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.4
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 12,
    border: "1px solid #e7d8d3",
    outline: "none",
    fontSize: 15,
    color: "#0f172a",
    background: "rgba(255,255,255,0.92)",
    boxSizing: "border-box"
  },
  button: {
    padding: "12px 16px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
    boxShadow: "0 8px 20px rgba(242,95,76,0.22)"
  },
  message: {
    margin: 0,
    color: "#166534",
    fontSize: 14,
    fontWeight: 500
  },
  upgradeBtn: {
    padding: "13px 16px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
    boxShadow: "0 10px 24px rgba(242,95,76,0.28)"
  },
  premiumBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 999,
    background: "rgba(22,101,52,0.08)",
    color: "#166534",
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 4
  },
  manageBtn: {
    padding: "11px 16px",
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    background: "transparent",
    color: "#475569",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14
  }
};
